import { useEffect, useState } from 'react';
import './ProgramPages.css';
import {
  IconAtom,
  IconBriefcase,
  IconCap,
  IconChart,
  IconCheck,
  IconGlobe,
  IconLeaf,
  IconStar,
  IconTarget,
} from './ProgramIcons';
import heroImage1 from './assets/news/new2.jpg';
import heroImage2 from './assets/news/news3.jpg';
import heroImage3 from './assets/news/news4.jpg';
import labsImage from './assets/labs1.png';

const ICON_MAP = {
  cap: IconCap,
  star: IconStar,
  atom: IconAtom,
  globe: IconGlobe,
  chart: IconChart,
  target: IconTarget,
  briefcase: IconBriefcase,
  leaf: IconLeaf,
};

function SecondaryPage({ page, onNavigate }) {
  const [activeHero, setActiveHero] = useState(0);
  
  // CMS data comes as { hero: {...}, overview: {...}, ... } (nested by section)
  // The page prop from App.js is already page?.en or the raw parsed object
  const cms = page || {};

  // Hero stats from CMS or hardcoded defaults
  const heroStats = (cms.hero_stats && cms.hero_stats.length > 0)
    ? cms.hero_stats
    : (cms.hero?.stats && cms.hero.stats.length > 0 ? cms.hero.stats : [
        { number: '25', label: 'Maximum Class' },
        { number: '2', label: 'Curriculum', sublabel: 'Cambridge + National (SPK Track)' },
        { number: 'LEAPS', label: 'Student Development' },
      ]);

  // Merge CMS data with hardcoded defaults
  const rawHighlights = (Array.isArray(cms.program_highlights) && cms.program_highlights.length > 0) ? cms.program_highlights : null;
  const defaultHighlights = [
    {
      icon: IconCap,
      title: 'University Preparation',
      description: 'Rigorous academics aligned with Cambridge IGCSE and A-Level standards for global university readiness.',
    },
    {
      icon: IconStar,
      title: 'Character Shaping Program',
      description: 'A structured Character Shaping Program integrates Eastern Values, Social-Emotional Learning, and Well-Being to cultivate integrity, resilience, compassion, responsibility, and ethical decision-making.',
    },
    {
      icon: IconAtom,
      title: 'Applied Learning',
      description: 'Students extend their learning through interdisciplinary subjects and research that deepen understanding, encourage intellectual curiosity, and prepare them for future challenges.',
    },
    {
      icon: IconGlobe,
      title: 'Students Development Program',
      description: 'The LEAPS Framework provides opportunities for leadership, extracurricular involvement, achievement, participation, and service, empowering students to grow as confident, responsible, compassionate, resilient, and globally aware individual.',
    },
  ];
  const programHighlights = rawHighlights ? rawHighlights.map((h, i) => ({ ...h, icon: (typeof h.icon === 'string' ? ICON_MAP[h.icon] : h.icon) || defaultHighlights[i % defaultHighlights.length].icon })) : defaultHighlights;

  const rawPathways = (Array.isArray(cms.pathways) && cms.pathways.length > 0) ? cms.pathways : null;
  const defaultPathways = [
    {
      level: 'Lower Secondary (Grades 7-8)',
      title: 'Foundation & Exploration',
      description: 'Students develop broad academic knowledge and skills that prepares them for Cambridge IGCSE.',
      features: [
        'Balanced curriculum',
        'Study skills and self-management',
        'Character and habits building',
      ],
      image: labsImage,
    },
    {
      level: 'Upper Secondary (Grades 9-10)',
      title: 'Specialization & Mastery',
      description: 'Students deepen their learning through subject specialisation and Cambridge IGCSE, preparing for higher education pathways.',
      features: [
        'Subject specialization',
        'IGCSE readiness and rigor',
        'Immersion and real-world exposure',
        'Interdisciplinary project depth',
        'Introduction to career pathways',
      ],
      image: labsImage,
    },
    {
      level: 'Pre University (Grades 11-12)',
      title: 'Advanced Study and University Preparation',
      description: 'Students deepen their learning through A/AS level specialisation, preparing for university admission and life beyond school.',
      features: [
        'Advanced Subject Mastery',
        'University and career counseling',
        'AS/A Level exam readiness',
        'Research and Innovation',
        'Globally ready graduates',
      ],
      image: labsImage,
    },
  ];
  const pathways = rawPathways ? rawPathways.map((p, i) => ({ ...p, image: p.image || defaultPathways[i % defaultPathways.length]?.image || labsImage })) : defaultPathways;

  const subjectTracks = (Array.isArray(cms.subject_tracks) && cms.subject_tracks.length > 0) ? cms.subject_tracks : [
    {
      track: 'Science & Health Track',
      subjects: ['Biology', 'Chemistry', 'Physics'],
      careers: 'Medicine, Pharmacy, Nursing, Biotechnology, Veterinary Science, Public Health, Sport Science',
    },
    {
      track: 'Engineering & Technology Track',
      subjects: ['Physics', 'Additional Mathematics', 'Computer Science'],
      careers: 'Engineering, Software Development, Data Science, Robotics, Architecture',
    },
    {
      track: 'Business & Economics Track',
      subjects: ['Accounting', 'Business Studies', 'Economics'],
      careers: 'Business Management, Finance & Banking, Entrepreneurship, Economics & Public Policy',
    },
    {
      track: 'Creative Arts Track',
      subjects: ['Art & Design', 'Music', 'Drama'],
      careers: 'Graphic Design, Film & Media Production, Performing Arts, Music Production, Fashion Design',
    },
    {
      track: 'Language & Communication Track',
      subjects: ['English (EFL/ESL)', 'Mandarin/German', 'Bahasa Indonesia'],
      careers: 'Tourism & Hospitality, Language Teaching, Content Writing, Cross-Cultural Communication',
    },
    {
      track: 'Humanities & Global Studies Track',
      subjects: ['Humanities and Global Perspective', 'History', 'Geography'],
      careers: 'Law, Political Science, Diplomacy, Social Work, Journalism, International Relations, Public Policy',
    },
    {
      track: 'Sport and Fitness Track',
      subjects: ['Physical Education'],
      careers: 'Physiotherapy, Coaching, Sports Management',
    },
  ];

  const studentSupport = (cms.support?.items || [
    {
      title: 'Academic Counseling',
      description: 'One-on-one guidance for course selection, study strategies, and academic goals.',
      icon: 'chart',
    },
    {
      title: 'University Guidance',
      description: 'Expert support for university applications, essays, and scholarship opportunities.',
      icon: 'target',
    },
    {
      title: 'Career Counseling',
      description: 'Career assessments, internships, and connections with industry professionals.',
      icon: 'briefcase',
    },
    {
      title: 'Personal Development',
      description: 'Mentoring, wellbeing support, and character formation programs.',
      icon: 'leaf',
    },
  ]).map(item => ({
    ...item,
    icon: ICON_MAP[item.icon] || IconChart,
  }));

  const achievements = cms.achievements?.items || [
    { number: '95%', label: 'University Acceptance Rate' },
    { number: '85%', label: 'Cambridge A*/A Grades' },
    { number: '20+', label: 'University Destinations' },
    { number: '100%', label: 'Graduate Employment/Study' },
  ];

  const opportunities = cms.opportunities?.items || [
    'Model United Nations',
    'Science Olympiads',
    'Debate & Public Speaking',
    'Sports Competitions',
    'Arts & Music Performances',
    'Community Service Projects',
    'Student Exchange Programs',
    'Entrepreneurship Club',
  ];

  const heroSlides = (cms.hero?.images && cms.hero.images.length > 0)
    ? cms.hero.images.map(img => typeof img === 'string' ? img : img.url || img)
    : [heroImage1, heroImage2, heroImage3];

  useEffect(() => {
    const intervalId = setInterval(() => {
      setActiveHero((current) => (current + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(intervalId);
  }, [heroSlides.length]);

  return (
    <div className="program-page secondary-page" id="secondary">
      {/* Hero Section */}
      <section className="program-hero">
        <div className="program-hero-media">
          {heroSlides.map((slide, index) => (
            <div
              key={index}
              className={`program-hero-slide ${index === activeHero ? 'active' : ''}`}
            >
              <img src={slide} alt="Secondary Program" />
            </div>
          ))}
        </div>
        <div className="program-hero-overlay" />
        <div className="program-hero-content">
          <span className="program-hero-badge">{cms.hero?.badge || 'Grades 7-12'}</span>
          <h1>{cms.hero?.heading || 'Secondary Program'}</h1>
          <p className="program-hero-subtitle">
            {cms.hero?.subtitle || 'Preparing confident, capable leaders for university and beyond'}
          </p>
          <div className="program-hero-stats">
            {heroStats.map((stat, index) => (
              <div key={index} className="program-stat">
                <span className="stat-number">{stat.number}</span>
                <span className="stat-label">{stat.label}</span>
                {stat.sublabel && <span className="stat-sublabel">{stat.sublabel}</span>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Program Overview */}
      <section className="program-overview">
        <div className="program-container">
          <div className="section-header">
            <span className="section-kicker">Program Overview</span>
            <h2>{cms.overview?.heading || 'Secondary Excellence'}</h2>
            <p>
              {cms.overview?.description || 'Permata Harapan Secondary provides a comprehensive education that integrates academic excellence, character formation, and holistic student development. Through the Cambridge and National Curriculum, the 5E Instructional Model, and a structured Character Shaping and Student Development Program (LEAPS), students develop the knowledge, competencies and values required to succeed in higher education and contribute to an increasingly interconnected world.'}
            </p>
          </div>

          <div className="program-highlights-grid">
            {programHighlights.map((item, index) => {
              const Icon = item.icon;
              return (
              <div className="highlight-card" key={index}>
                <div className="highlight-icon" aria-hidden="true">
                  <Icon />
                </div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pathways */}
      <section className="pathways-section">
        <div className="program-container">
          <div className="section-header">
            <span className="section-kicker">Academic Pathways</span>
            <h2>{cms.pathways?.heading || 'Junior High to Senior High'}</h2>
            <p>{cms.pathways?.description || 'A progressive journey from foundation to university readiness'}</p>
          </div>

          <div className="pathways-grid">
            {pathways.map((pathway, index) => (
              <div className="pathway-card" key={index}>
                <div className="pathway-image">
                  <img src={pathway.image} alt={pathway.level} />
                </div>
                <div className="pathway-content">
                  <span className="pathway-level">{pathway.level}</span>
                  <h3>{pathway.title}</h3>
                  <p>{pathway.description}</p>
                  <ul className="pathway-features">
                    {pathway.features.map((feature, idx) => (
                      <li key={idx}>
                        <span className="check-icon" aria-hidden="true">
                          <IconCheck />
                        </span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Subject Tracks */}
      <section className="tracks-section">
        <div className="program-container">
          <div className="section-header">
            <span className="section-kicker">Specialization</span>
            <h2>{cms.tracks?.heading || 'Subject Tracks'}</h2>
            <p>{cms.tracks?.description || 'Choose your pathway based on interests and university goals'}</p>
          </div>

          <div className="tracks-grid">
            {subjectTracks.map((track, index) => (
              <div className="track-card" key={index}>
                <h3>{track.track}</h3>
                <div className="track-subjects">
                  {track.subjects.map((subject, idx) => (
                    <span className="subject-tag" key={idx}>{subject}</span>
                  ))}
                </div>
                <div className="track-careers">
                  <strong>Career Paths:</strong>
                  <p>{track.careers}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Student Support */}
      <section className="support-section" style={{ display: 'none' }}>
        <div className="program-container">
          <div className="section-header">
            <span className="section-kicker">Student Support</span>
            <h2>{cms.support?.heading || 'Guidance & Counseling'}</h2>
            <p>{cms.support?.description || 'Comprehensive support for academic, personal, and career development'}</p>
          </div>

          <div className="support-grid">
            {studentSupport.map((support, index) => {
              const Icon = support.icon;
              return (
                <div className="support-card" key={index}>
                  <div className="support-icon" aria-hidden="true">
                    <Icon />
                  </div>
                  <h4>{support.title}</h4>
                  <p>{support.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Achievements */}
      <section className="achievements-section" style={{ display: 'none' }}>
        <div className="program-container">
          <div className="section-header">
            <span className="section-kicker">Our Track Record</span>
            <h2>{cms.achievements?.heading || 'Student Success'}</h2>
            <p>{cms.achievements?.description || 'Results that speak for themselves'}</p>
          </div>

          <div className="achievements-grid">
            {achievements.map((achievement, index) => (
              <div className="achievement-card" key={index}>
                <div className="achievement-number">{achievement.number}</div>
                <div className="achievement-label">{achievement.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Opportunities */}
      <section className="opportunities-section" style={{ display: 'none' }}>
        <div className="program-container">
          <div className="section-header">
            <span className="section-kicker">Beyond Academics</span>
            <h2>{cms.opportunities?.heading || 'Leadership & Enrichment'}</h2>
            <p>{cms.opportunities?.description || 'Opportunities to grow, compete, and make a difference'}</p>
          </div>

          <div className="opportunities-grid">
            {opportunities.map((opportunity, index) => (
              <div className="opportunity-card" key={index}>
                <span className="opportunity-icon" aria-hidden="true">
                  <IconStar />
                </span>
                <span className="opportunity-name">{opportunity}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="parent-info" style={{ display: 'none' }}>
        <div className="program-container">
          <div className="cta-section">
            <h3>{cms.cta?.heading || 'Ready to Begin Your Secondary Journey?'}</h3>
            <p>{cms.cta?.description || 'Join a community of ambitious learners preparing for global success'}</p>
            <div className="cta-buttons">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => onNavigate && onNavigate('register')}
              >
                Apply Now
              </button>
              <a href="/contact" className="btn btn-secondary">Book a Campus Tour</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default SecondaryPage;
